import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';

const labels = { verified: 'Verified', needs_review: 'Needs review', unable_to_verify: 'Unable to verify' };
function Source({ url }) {
  try {
    const parsed = new URL(url);
    if (['https:', 'http:'].includes(parsed.protocol)) return <a href={parsed.href} target="_blank" rel="noopener noreferrer">{url}</a>;
  } catch { /* Stored values may be bare domains or malformed. */ }
  return <span>{String(url)}</span>;
}

export default function IdentityAuditPanel({ onProgress }) {
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState('all');
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const lastChecked = useRef(null);
  const load = useCallback(async () => {
    const request = ++generation.current;
    try {
      const [next, rows] = await Promise.all([api.getIdentityAudit(), api.getIdentityAuditResults(filter, offset)]);
      if (request !== generation.current) return;
      setSummary(next); setResults(rows); setError('');
      if (lastChecked.current !== next.checked) {
        if (lastChecked.current !== null && onProgress) onProgress();
        lastChecked.current = next.checked;
      }
    } catch (e) { if (request === generation.current) setError(e.message || 'Could not load audit progress.'); }
  }, [filter, offset, onProgress]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => { clearInterval(timer); generation.current += 1; };
  }, [load]);

  const control = async action => {
    if (['pilot', 'remaining'].includes(action) && !window.confirm(action === 'pilot'
      ? 'Audit up to 50 existing brands across all organisations? Current data will be preserved. Configured search and social APIs may use provider quota.'
      : 'Audit every remaining brand in the saved cohort? Completed records will be skipped. Configured search and social APIs may use provider quota.')) return;
    setBusy(true); setError('');
    try { await api.controlIdentityAudit(action); await load(); }
    catch (e) { setError(e.message || 'Could not update audit.'); }
    finally { setBusy(false); }
  };
  const running = summary?.status === 'running' && !summary?.recoverable;
  return <section className="card" style={{ padding: 20, gap: 14 }} aria-label="Existing brand audit">
    <h2 className="o-card-head-title">Audit existing brands</h2>
    <p>One-time website and social-account check across all organisations. The pilot checks 50 brands; Audit remaining brands continues the same saved list. Current data stays unchanged.</p>
    {error && <p role="alert">{error}</p>}
    {!summary ? <p role="status">Loading audit progress…</p> : <>
      <div role="status"><strong>{summary.checked} of {summary.total} checked</strong> · {summary.remaining} remaining · {summary.status.replaceAll('_', ' ')}{summary.recoverable ? ' — interrupted; ready to resume' : ''}</div>
      <progress aria-label="Audit progress" value={summary.checked} max={summary.total || 1} style={{ width: '100%' }} />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{Object.entries(labels).map(([key, label]) => <span key={key}>{label}: {summary.counts[key] || 0}</span>)}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={busy || running || summary.target > 0 || !summary.remaining} onClick={() => control('pilot')}>Run pilot — 50 brands</button>
        <button className="btn btn-secondary" disabled={busy || running || !summary.remaining} onClick={() => control('remaining')}>Audit remaining brands</button>
        {running ? <button className="btn btn-secondary" disabled={busy} onClick={() => control('pause')}>Pause</button>
          : summary.remaining > 0 && summary.target > summary.checked && <button className="btn btn-secondary" disabled={busy} onClick={() => control('resume')}>Resume saved batch</button>}
      </div>
      <p style={{ fontSize: 13 }}>Pause saves completed checks; an in-progress check may finish. If the server restarts, Resume becomes available within 10 minutes. Brands added after the audit starts are outside this one-time list.</p>
      <p style={{ fontSize: 13 }}>Verified means the automated evidence checks passed. Blocked pages or missing evidence are not counted as incorrect. Suggested corrections appear in the review queue below; other findings remain here for inspection.</p>
      <label>Audit results{' '}<select aria-label="Audit result filter" value={filter} onChange={e => { setFilter(e.target.value); setOffset(0); }}>
        <option value="all">All checked records</option>{Object.entries(labels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      {results.items.map(item => <details key={item.id} style={{ borderTop: '1px solid var(--border-color, #ddd)', paddingTop: 10 }}>
        <summary><strong>{item.name}</strong> · {item.organization} · {labels[item.status]}{item.proposal_id ? ` · Proposal #${item.proposal_id}` : ''}</summary>
        <p>{item.report.reason}</p>
        {Object.entries(item.report.checks || {}).map(([field, check]) => <div key={field} style={{ marginBottom: 8 }}>
          <strong>{field.replaceAll('_', ' ')}: {labels[check.status]}</strong><p>{check.reason}</p>
          {check.candidate && <p>Suggested: {check.candidate}</p>}
          {check.backlink && <p>Profile backlink: <Source url={check.backlink} /></p>}
          {check.records && <ul>{check.records.map(record => <li key={record.brand_id}>{record.name} (#{record.brand_id}): {record.fields.join(', ')}</li>)}</ul>}
        </div>)}
        {(item.report.citations || []).map((url, index) => <p key={index}><Source url={url} /></p>)}
        <p>Checked {new Date(item.checked_at).toLocaleString()}</p>
      </details>)}
      {!results.items.length && <p>No audit results in this view yet.</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous results</button>
        <button className="btn btn-secondary" disabled={offset + 25 >= results.total} onClick={() => setOffset(offset + 25)}>Next results</button>
      </div>
    </>}
  </section>;
}
